/**
 * Client API pour le AI Service
 * Couche d'abstraction pour les appels HTTP bruts
 */

import { httpClientLong, API_ENDPOINTS } from '@sphere/shared/data-access/http'
import type { SupportedLanguage } from '@sphere/ai/domain'

// GenerateExerciseRequest utilise File (navigateur) donc reste dans le frontend
// Le type sera importé depuis le frontend lors de l'utilisation
export interface GenerateExerciseRequest {
  file: File
  lang?: string
}

// ============================================================================
// AI Client
// ============================================================================
// NOTE: Pour tous les uploads de fichiers avec FormData, nous définissons
// Content-Type: undefined pour permettre à Axios d'ajouter automatiquement
// le header 'multipart/form-data' avec le bon boundary. Sans cela, le serveur
// FastAPI ne peut pas parser correctement les fichiers uploadés.

export const aiClient = {
  quiz: {
    generate: (file: File, lang: SupportedLanguage) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('lang', lang)
      return httpClientLong.post(API_ENDPOINTS.AI.QUIZ, formData, {
        headers: { 'Content-Type': undefined },
      })
    },

    generateCloze: (file: File, lang: SupportedLanguage) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('lang', lang)
      return httpClientLong.post(
        API_ENDPOINTS.AI.CLOZE_QUIZ_GENERATOR,
        formData,
        {
          headers: { 'Content-Type': undefined },
        }
      )
    },
  },

  summary: {
    generate: (file: File, lang: SupportedLanguage) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('lang', lang)
      return httpClientLong.post(API_ENDPOINTS.AI.SUMMARY, formData, {
        headers: { 'Content-Type': undefined },
      })
    },
  },

  pdf: {
    generateExercise: (dto: GenerateExerciseRequest) => {
      const formData = new FormData()
      formData.append('file', dto.file)
      if (dto.lang) formData.append('lang', dto.lang)
      return httpClientLong.post(API_ENDPOINTS.AI.EXERCISE_PDF, formData, {
        headers: { 'Content-Type': undefined },
        responseType: 'blob',
      })
    },

    generateQA: (file: File, lang: SupportedLanguage) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('lang', lang)
      return httpClientLong.post(API_ENDPOINTS.AI.QA_PDF, formData, {
        headers: { 'Content-Type': undefined },
        responseType: 'blob',
      })
    },
  },

  grade: {
    gradeCopy: (
      answerKey: File,
      studentCopy: File,
      totalPoints: number,
      lang: SupportedLanguage,
    ) => {
      const formData = new FormData()
      formData.append('answer_key', answerKey)
      formData.append('student_copy', studentCopy)
      formData.append('total_points', String(totalPoints))
      formData.append('lang', lang)
      return httpClientLong.post(API_ENDPOINTS.AI.GRADE_COPY, formData, {
        headers: { 'Content-Type': undefined },
      })
    },
  },

  health: {
    check: () => httpClientLong.get(API_ENDPOINTS.AI.HEALTH, { timeout: 5000 }),
  },
}
